/**
 * @fileoverview Student-portal assignments checklist (Spec 01): open
 * assignments grouped by teacher, with progress bars for tracked targets and
 * a manual "Mark done" on target-less items. Untagged practice you log counts
 * toward your assignments automatically.
 */

import { useState } from "react";
import { toast } from "sonner";
import studentApi, { getStudentErrorMessage } from "@/lib/studentApi";
import { assignmentPercent, assignmentProgressLabel, hasAutoTargets } from "@/lib/assignments";
import { Card, Icon } from "@/components/studio/primitives";

const ChecklistRow = ({ assignment, onDone, marking, borderBottom }) => {
  const percent = assignmentPercent(assignment);
  const done = assignment.status === "completed";
  return (
    <div
      className={`px-[18px] py-3.5 ${borderBottom ? "border-b border-[var(--studio-divider)]" : ""}`}
      data-testid={`checklist-row-${assignment.assignment_id}`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
            done
              ? "border-[var(--state-success-border)] bg-[var(--state-success-dim)] text-[var(--state-success)]"
              : "border-[var(--studio-divider-strong)] text-transparent"
          }`}
        >
          <Icon name="check" size={12} />
        </span>
        <div className="min-w-0 flex-1">
          <p className={`font-sans text-sm ${done ? "text-[var(--text-tertiary)] line-through" : "text-[var(--text-primary)]"}`}>
            {assignment.text}
          </p>
          {assignmentProgressLabel(assignment) && (
            <p className="mt-1 font-mono text-[12px] text-[var(--text-tertiary)]">
              {assignmentProgressLabel(assignment)}
            </p>
          )}
          {percent !== null && !done && (
            <div className="mt-2 h-1 overflow-hidden rounded-full bg-[var(--studio-interactive)]">
              <div
                className="h-full rounded-full bg-[var(--accent-yellow)] transition-[width] duration-300"
                style={{ width: `${percent}%` }}
                data-testid={`checklist-progress-${assignment.assignment_id}`}
              />
            </div>
          )}
        </div>
        {!done && !hasAutoTargets(assignment) && onDone && (
          <button
            type="button"
            disabled={marking === assignment.assignment_id}
            onClick={() => onDone(assignment)}
            className="shrink-0 rounded-full border border-[var(--studio-divider)] px-3 py-1 font-sans text-xs text-[var(--text-secondary)] hover:bg-[var(--studio-interactive)] disabled:opacity-50"
            data-testid={`mark-done-${assignment.assignment_id}`}
          >
            Mark done
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Renders open assignments as a checklist. When `grouped` (default), items are
 * grouped by teacher_name; pass grouped={false} for a flat list (lesson view).
 * Calls `onChanged` after a successful mark-done so the parent can refetch.
 */
export function StudentAssignmentsChecklist({ assignments, grouped = true, onChanged }) {
  const [marking, setMarking] = useState(null);

  if (!assignments || assignments.length === 0) return null;

  const markDone = async (assignment) => {
    setMarking(assignment.assignment_id);
    try {
      await studentApi.post(`/student/assignments/${assignment.assignment_id}/complete`);
      toast.success("Nice work — marked done!");
      onChanged?.();
    } catch (error) {
      toast.error(getStudentErrorMessage(error, "Could not mark this done"));
    } finally {
      setMarking(null);
    }
  };

  if (!grouped) {
    return (
      <Card padding={0} data-testid="assignments-checklist">
        {assignments.map((a, i) => (
          <ChecklistRow key={a.assignment_id} assignment={a} onDone={markDone} marking={marking} borderBottom={i < assignments.length - 1} />
        ))}
      </Card>
    );
  }

  const byTeacher = new Map();
  assignments.forEach((a) => {
    const key = a.teacher_name || "Your teacher";
    if (!byTeacher.has(key)) byTeacher.set(key, []);
    byTeacher.get(key).push(a);
  });

  return (
    <Card padding={0} data-testid="assignments-checklist">
      <div className="flex items-center gap-2 border-b border-[var(--studio-divider)] px-[18px] py-3.5">
        <Icon name="target" size={16} className="text-[var(--accent-yellow-text)]" />
        <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">
          Your assignments
        </span>
      </div>
      {[...byTeacher.entries()].map(([teacher, items], gi, groups) => (
        <div key={teacher}>
          {byTeacher.size > 1 && (
            <p className="px-[18px] pt-3 font-sans text-[12px] font-medium text-[var(--text-tertiary)]" data-testid={`checklist-teacher-${gi}`}>
              From {teacher}
            </p>
          )}
          {items.map((a, i) => (
            <ChecklistRow
              key={a.assignment_id}
              assignment={a}
              onDone={markDone}
              marking={marking}
              borderBottom={i < items.length - 1 || gi < groups.length - 1}
            />
          ))}
        </div>
      ))}
      <p className="border-t border-[var(--studio-divider)] px-[18px] py-2.5 font-sans text-[11px] text-[var(--text-quaternary)]">
        Practice you log counts toward your assignments automatically.
      </p>
    </Card>
  );
}
